/**
 * Audit Log — compliance / forensics timeline of every admin mutation.
 *
 * Surfaces actions logged via `core.audit.log_admin_action(...)` on the
 * backend. Filterable by action verb + target type, newest first.
 */
import { useEffect, useState, useMemo } from "react";
import { ClipboardList, RefreshCw, Filter, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const ACTION_COLORS = {
  // Auth
  admin_login_success:      "bg-emerald-50 text-emerald-700",
  // Security ops — destructive / sensitive
  jwt_secret_generated:     "bg-rose-50 text-rose-700",
  login_bucket_reset:       "bg-amber-50 text-amber-700",
  pending_queue_archived:   "bg-rose-50 text-rose-700",
  // Products
  product_created:          "bg-sky-50 text-sky-700",
  product_deleted:          "bg-rose-50 text-rose-700",
  product_visibility_changed: "bg-amber-50 text-amber-700",
  products_reordered:       "bg-sky-50 text-sky-700",
  product_image_updated:    "bg-violet-50 text-violet-700",
  product_image_uploaded:   "bg-violet-50 text-violet-700",
  // Announcements
  announcement_created:     "bg-sky-50 text-sky-700",
  announcement_updated:     "bg-amber-50 text-amber-700",
  announcement_deleted:     "bg-rose-50 text-rose-700",
  // Reviews
  review_approved:          "bg-emerald-50 text-emerald-700",
  review_rejected:          "bg-rose-50 text-rose-700",
  // PayPal / scheduler
  paypal_reconcile_run:     "bg-slate-100 text-slate-700",
};

const pill = (action) => ACTION_COLORS[action] || "bg-slate-100 text-slate-600";

const formatAction = (a) => (a || "").replace(/_/g, " ").toUpperCase();

const formatTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  } catch {
    return iso;
  }
};

const formatDetails = (details) => {
  if (!details || Object.keys(details).length === 0) return "—";
  return Object.entries(details)
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(" · ");
};

export const AuditLogTab = () => {
  const [data, setData] = useState({ items: [], total: 0, available_actions: [], available_target_types: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [targetFilter, setTargetFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.set("action", actionFilter);
      if (targetFilter) params.set("target_type", targetFilter);
      params.set("limit", "200");
      const { data: res } = await adminApi.get(`/admin/audit-log?${params.toString()}`);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  };

  // Stream the CSV via the admin axios instance so the JWT bearer header is
  // attached automatically — a plain <a href> would 401 because cookies
  // aren't used for admin auth.
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.set("action", actionFilter);
      if (targetFilter) params.set("target_type", targetFilter);
      const { data: blob } = await adminApi.get(
        `/admin/audit-log/export.csv?${params.toString()}`,
        { responseType: "blob" },
      );
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const filename = `audit_log_${today}.csv`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { load(); }, [actionFilter, targetFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data.items;
    const needle = search.toLowerCase();
    return data.items.filter((row) =>
      [row.action, row.actor_ip, row.actor_email, row.target_id, JSON.stringify(row.details)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [data.items, search]);

  return (
    <div data-testid="audit-log-panel" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#0A192F]">
            <ClipboardList size={18} className="text-[#C5A059]" />
            <h2 className="font-accent text-sm tracking-wider">AUDIT LOG</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Every admin mutation — who did what, when. Newest first. Showing{" "}
            <strong>{data.returned ?? 0}</strong> of <strong>{data.total ?? 0}</strong> total events.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export → Dark Navy bg + Gold border + Gold icon. Hovers brighten
              the border to the full #D4AF37 gold so it picks up an elite,
              "premium compliance report" feel. */}
          <button
            data-testid="audit-log-export-csv-btn"
            onClick={handleExport}
            disabled={exporting || (data.total ?? 0) === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A192F] text-white text-[11px] font-accent tracking-wider border border-[#C5A059] hover:border-[#D4AF37] hover:shadow-[0_0_0_1px_#D4AF37] disabled:opacity-50 disabled:cursor-not-allowed transition-shadow"
            title={(data.total ?? 0) === 0 ? "Nothing to export yet" : "Download CSV report"}
          >
            {exporting ? (
              <Loader2 size={12} className="animate-spin text-[#D4AF37]" />
            ) : (
              <Download size={12} className="text-[#D4AF37]" />
            )}
            {exporting ? "EXPORTING…" : "EXPORT TO CSV"}
          </button>
          <button
            data-testid="audit-log-reload-btn"
            onClick={load}
            className="text-xs text-slate-500 hover:text-[#0A192F] flex items-center gap-1"
          >
            <RefreshCw size={12} /> Reload
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">
            <Filter size={9} className="inline mr-1" /> ACTION
          </label>
          <select
            data-testid="audit-log-action-filter"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full text-sm p-2 border border-slate-300 focus:border-[#C5A059] focus:outline-none bg-white"
          >
            <option value="">All actions</option>
            {(data.available_actions || []).map((a) => (
              <option key={a} value={a}>{formatAction(a)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">
            <Filter size={9} className="inline mr-1" /> TARGET TYPE
          </label>
          <select
            data-testid="audit-log-target-filter"
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
            className="w-full text-sm p-2 border border-slate-300 focus:border-[#C5A059] focus:outline-none bg-white"
          >
            <option value="">All targets</option>
            {(data.available_target_types || []).map((t) => (
              <option key={t} value={t}>{t.toUpperCase()}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">
            FREE-TEXT SEARCH
          </label>
          <input
            data-testid="audit-log-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="IP, ID, value…"
            className="w-full text-sm p-2 border border-slate-300 focus:border-[#C5A059] focus:outline-none"
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        {loading ? (
          <div className="px-5 py-8 text-center text-slate-400 text-xs">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-400 text-xs">
            No audit events match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-2 font-accent text-[10px] w-44">WHEN</th>
                <th className="px-5 py-2 font-accent text-[10px] w-48">ACTION</th>
                <th className="px-5 py-2 font-accent text-[10px]">TARGET</th>
                <th className="px-5 py-2 font-accent text-[10px] w-32">ACTOR IP</th>
                <th className="px-5 py-2 font-accent text-[10px]">DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  data-testid={`audit-row-${row.id}`}
                  className="border-t border-slate-100 align-top"
                >
                  <td className="px-5 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                    {formatTs(row.ts)}
                  </td>
                  <td className="px-5 py-2.5">
                    <span
                      className={`inline-block text-[10px] font-accent tracking-wider px-2 py-0.5 ${pill(row.action)}`}
                    >
                      {formatAction(row.action)}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-xs">
                    {row.target_type && (
                      <div className="text-slate-500 uppercase font-accent text-[10px]">
                        {row.target_type}
                      </div>
                    )}
                    {row.target_id && (
                      <code className="text-[11px] text-[#0A192F] break-all">{row.target_id}</code>
                    )}
                    {!row.target_type && !row.target_id && <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-5 py-2.5 text-xs font-mono text-slate-600 whitespace-nowrap">
                    {row.actor_ip || "—"}
                  </td>
                  <td className="px-5 py-2.5 text-[11px] text-slate-600 break-all max-w-md">
                    {formatDetails(row.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
